import { useEffect, useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { toast } from "react-hot-toast";
import { TextInputComp, FormikTextAreaComp } from "../..";
import { FileUploaderComp } from "../../Ui";
import AdminEditorStepper from "./AdminEditorStepper";
import AdminContentSectionEditor from "./AdminContentSectionEditor";
import AdminPublicPreviewAction from "./AdminPublicPreviewAction";
import InvestorPortalActionButton from "./InvestorPortalActionButton";
import { DetailSection } from "./InvestorPortalDetailFields";
import { OPPORTUNITY_STATUS, OPPORTUNITY_EDITOR_STEPS } from "../../../store/admin/investorPortal/constants";
import { formatMoney } from "../../../utils/currencyFormat";
import { getDateFieldMins, isHttpImageUrl } from "../../../utils/opportunityDateValidation";
import {
  deriveCompletedSteps,
  getReviewChecklist,
  publishReadySchema,
  stepSchemas,
} from "../../../utils/opportunityEditorValidation";

function toFormValues(opp) {
  return {
    title: opp?.title || "",
    slug: opp?.slug || "",
    category: opp?.category || "",
    shortDescription: opp?.shortDescription || "",
    fullDescription: opp?.fullDescription || "",
    providerName: opp?.providerName || "",
    riskLevel: opp?.riskLevel || "Moderate",
    status: opp?.status || OPPORTUNITY_STATUS.DRAFT,
    unitPriceNgn: opp?.unitPriceNgn ?? "",
    expectedReturnPct: opp?.expectedReturnPct ?? "",
    durationMonths: opp?.durationMonths ?? "",
    minUnits: opp?.minUnits ?? 1,
    maxUnits: opp?.maxUnits ?? 100,
    totalUnits: opp?.totalUnits ?? "",
    payoutType: opp?.payoutType || "capital_plus_return",
    currency: opp?.currency || "NGN",
    fundingOpenDate: opp?.fundingOpenDate || "",
    fundingCloseDate: opp?.fundingCloseDate || "",
    investmentStartDate: opp?.investmentStartDate || "",
    maturityDate: opp?.maturityDate || "",
    returnPayoutDate: opp?.returnPayoutDate || "",
    scheduledPublishAt: opp?.scheduledPublishAt || "",
    coverImageUrl: opp?.coverImageUrl || "",
    overview: opp?.overview || "",
    fundsUsage: opp?.fundsUsage || "",
    highlights: opp?.highlights || [],
    howItWorks: opp?.howItWorks || [],
    gallery: opp?.gallery || [],
    milestones: opp?.milestones || [],
    terms: opp?.terms || "",
    faqs: opp?.faqs || [],
    riskDisclaimer: opp?.riskDisclaimer || "",
    id: opp?.id,
  };
}

function DraftStatusIndicator({ status }) {
  if (!status || status === "idle") return null;
  const labels = {
    saving: "Saving…",
    saved: "Draft saved",
    failed: "Save failed",
  };
  return (
    <span
      className={`admin-draft-status admin-draft-status--${status}`}
      role="status"
      aria-live="polite"
    >
      {labels[status] || status}
    </span>
  );
}

function CoverImageField({ value, onChange, error }) {
  const [previewBroken, setPreviewBroken] = useState(false);
  const validUrl = isHttpImageUrl(value);

  useEffect(() => {
    setPreviewBroken(false);
  }, [value]);

  return (
    <div className="admin-cover-image-field">
      <p className="text-muted small mb-2">
        Recommended: 1600 × 900px, JPG, PNG or WebP, maximum 5MB. Upload an image or paste a
        publicly accessible URL.
      </p>

      <div className="mb-3">
        <FileUploaderComp
          LabelText="Upload cover image"
          bodyText={
            <span className="btn btn-outline-secondary btn-sm">
              {value ? "Replace image" : "Choose image"}
            </span>
          }
          onChange={(url) => {
            onChange(url || "");
            setPreviewBroken(false);
          }}
        />
      </div>

      <TextInputComp
        LabelText="Cover image URL"
        name="coverImageUrl"
        placeholder="https://example.com/investment-cover.jpg"
        desc="Paste a publicly accessible HTTP or HTTPS image URL."
      />

      {error ? <small className="text-danger d-block mb-2">{error}</small> : null}

      {value ? (
        <div className="admin-cover-preview mt-2">
          {validUrl && !previewBroken ? (
            <img
              src={value.trim()}
              alt="Cover preview"
              className="admin-cover-preview-img"
              onError={() => setPreviewBroken(true)}
            />
          ) : (
            <div className="admin-cover-preview-fallback">
              {previewBroken
                ? "Image could not be loaded. Check the URL or upload a new file."
                : "Enter a valid HTTP or HTTPS image URL to preview."}
            </div>
          )}
          <button
            type="button"
            className="btn btn-link btn-sm text-danger px-0 mt-1"
            onClick={() => onChange("")}
          >
            Remove image
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ReviewSummary({ values, onEditStep }) {
  const target = (Number(values.totalUnits) || 0) * (Number(values.unitPriceNgn) || 0);
  const { items, ready } = getReviewChecklist(values);
  const presentMissing = (ok) => (ok ? "Present" : "Missing");

  const Section = ({ title, step, children }) => (
    <div className="admin-review-section mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
        <h6 className="fw-semibold mb-0">{title}</h6>
        <button type="button" className="btn btn-link btn-sm px-0" onClick={() => onEditStep(step)}>
          Edit
        </button>
      </div>
      <div className="row g-2">{children}</div>
    </div>
  );

  const Field = ({ label, value }) => (
    <div className="col-md-6">
      <p className="small mb-1">
        <strong>{label}:</strong> {value ?? "—"}
      </p>
    </div>
  );

  return (
    <div className="admin-review-summary">
      <Section title="Basic information" step={0}>
        <Field label="Title" value={values.title || "—"} />
        <Field label="Category" value={values.category || "—"} />
        <Field label="Provider" value={values.providerName || "—"} />
        <Field label="Risk" value={values.riskLevel} />
        <Field label="Status" value={String(values.status || "").replace(/_/g, " ")} />
      </Section>

      <Section title="Financial terms" step={1}>
        <Field label="Unit price" value={formatMoney(Number(values.unitPriceNgn) || 0)} />
        <Field label="Total units" value={values.totalUnits || "—"} />
        <Field label="Funding target" value={formatMoney(target)} />
        <Field label="Expected return" value={`${values.expectedReturnPct || "—"}%`} />
        <Field label="Duration" value={`${values.durationMonths || "—"} months`} />
        <Field label="Min / max units" value={`${values.minUnits || "—"} / ${values.maxUnits || "—"}`} />
        <Field label="Payout type" value={String(values.payoutType || "").replace(/_/g, " ")} />
      </Section>

      <Section title="Timeline" step={2}>
        <Field label="Scheduled publish" value={values.scheduledPublishAt || "—"} />
        <Field label="Funding open" value={values.fundingOpenDate || "—"} />
        <Field label="Funding close" value={values.fundingCloseDate || "—"} />
        <Field label="Investment start" value={values.investmentStartDate || "—"} />
        <Field label="Maturity" value={values.maturityDate || "—"} />
        <Field label="Payout date" value={values.returnPayoutDate || "—"} />
      </Section>

      <Section title="Public content" step={3}>
        <div className="col-12 mb-2">
          {isHttpImageUrl(values.coverImageUrl) ? (
            <img
              src={values.coverImageUrl.trim()}
              alt=""
              className="admin-cover-preview-img admin-cover-preview-img--thumb"
            />
          ) : (
            <span className="small text-muted">No cover image</span>
          )}
        </div>
        <Field label="Overview" value={presentMissing(Boolean(values.overview?.trim()))} />
        <Field label="Highlights" value={`${values.highlights?.length || 0}`} />
        <Field label="How-it-works steps" value={`${values.howItWorks?.length || 0}`} />
        <Field label="Gallery" value={`${values.gallery?.length || 0}`} />
        <Field label="FAQs" value={`${values.faqs?.length || 0}`} />
        <Field label="Disclaimer" value={presentMissing(Boolean(values.riskDisclaimer?.trim()))} />
      </Section>

      <div className="admin-review-checklist">
        <h6 className="fw-semibold mb-2">Readiness checklist</h6>
        <ul className="list-unstyled mb-0">
          {items.map((item) => (
            <li key={item.key} className="d-flex align-items-start gap-2 mb-2 small">
              <span aria-hidden="true">{item.ok ? "✓" : item.blocking ? "✗" : "!"}</span>
              <span className={item.ok ? "text-success" : item.blocking ? "text-danger" : "text-warning"}>
                {item.label}
                {!item.ok && item.step != null ? (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => onEditStep(item.step)}
                    >
                      Edit
                    </button>
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
        {!ready ? (
          <p className="small text-muted mb-0 mt-2">
            Resolve blocking items before submitting for review or publishing.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function UnsavedLeaveGuard({ enabled }) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [enabled]);
  return null;
}

function EditorBody({
  step,
  setStep,
  completedSteps,
  setCompletedSteps,
  draftStatus,
  setDraftStatus,
  mode,
  onSave,
  onCancel,
  canPublishNow,
}) {
  const {
    values,
    setFieldValue,
    isSubmitting,
    setSubmitting,
    errors,
    setErrors,
    setTouched,
    dirty,
    resetForm,
  } = useFormikContext();
  const dateMins = getDateFieldMins(values);
  const checklist = useMemo(() => getReviewChecklist(values), [values]);

  const showFieldErrors = (fieldErrors) => {
    setErrors(fieldErrors);
    const touchedMap = Object.keys(fieldErrors).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    // TextInputComp only renders errors when meta.touched && meta.error
    setTouched(touchedMap, false);
  };

  const persistDraft = async (submitAction = "draft") => {
    const payload = {
      ...values,
      unitPriceNgn: values.unitPriceNgn === "" ? "" : Number(values.unitPriceNgn),
      expectedReturnPct: values.expectedReturnPct === "" ? "" : Number(values.expectedReturnPct),
      durationMonths: values.durationMonths === "" ? "" : Number(values.durationMonths),
      minUnits: Number(values.minUnits) || 1,
      maxUnits: Number(values.maxUnits) || 1,
      totalUnits: values.totalUnits === "" ? "" : Number(values.totalUnits),
      highlights: Array.isArray(values.highlights) ? values.highlights : [],
      status: OPPORTUNITY_STATUS.DRAFT,
    };

      setDraftStatus("saving");
    try {
      const saved = await onSave(payload, submitAction, { nextStep: step + 1 });
      setDraftStatus("saved");
      if (saved) {
        const nextValues = toFormValues({ ...values, ...saved, id: saved.id || values.id });
        resetForm({ values: nextValues });
      }
      return saved;
    } catch (error) {
      setDraftStatus("failed");
      throw error;
    }
  };

  const handleSaveAndContinue = async () => {
    try {
      await stepSchemas[step].validate(values, { abortEarly: false });
    } catch (validationError) {
      const fieldErrors = {};
      (validationError.inner || []).forEach((err) => {
        if (err.path && !fieldErrors[err.path]) fieldErrors[err.path] = err.message;
      });
      if (validationError.path && !fieldErrors[validationError.path]) {
        fieldErrors[validationError.path] = validationError.message;
      }
      showFieldErrors(fieldErrors);
      toast.error("Please fix the highlighted fields before continuing.");
      return;
    }

    setSubmitting(true);
    try {
      await persistDraft("draft");
      setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step].sort((a, b) => a - b)));
      toast.success("Draft saved");
      setStep((s) => Math.min(s + 1, OPPORTUNITY_EDITOR_STEPS.length - 1));
    } catch (error) {
      const message = error?.data?.message || error?.message || "Unable to save draft.";
      toast.error(message);
      const apiErrors = error?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, msgs]) => {
          const map = {
            funding_close_date: "fundingCloseDate",
            funding_open_date: "fundingOpenDate",
            investment_start_date: "investmentStartDate",
            maturity_date: "maturityDate",
            payout_date: "returnPayoutDate",
            scheduled_publish_date: "scheduledPublishAt",
            cover_image: "coverImageUrl",
            provider_name: "providerName",
            unit_price: "unitPriceNgn",
            expected_return_percentage: "expectedReturnPct",
            duration_months: "durationMonths",
            total_units: "totalUnits",
            minimum_units: "minUnits",
            maximum_units: "maxUnits",
            "public_content.overview": "overview",
          };
          mapped[map[key] || key] = Array.isArray(msgs) ? msgs[0] : String(msgs);
        });
        showFieldErrors(mapped);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLifecycleAction = async (submitAction) => {
    try {
      await publishReadySchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      const fieldErrors = {};
      (validationError.inner || []).forEach((err) => {
        if (err.path && !fieldErrors[err.path]) fieldErrors[err.path] = err.message;
      });
      if (validationError.path && !fieldErrors[validationError.path]) {
        fieldErrors[validationError.path] = validationError.message;
      }
      showFieldErrors(fieldErrors);
      toast.error("Complete all required fields before this action.");
      return;
    }

    setSubmitting(true);
    try {
      await persistDraft(submitAction);
      toast.success(
        submitAction === "publish"
          ? "Opportunity published"
          : submitAction === "review"
            ? "Submitted for internal review"
            : "Draft saved"
      );
    } catch (error) {
      toast.error(error?.data?.message || "Unable to complete this action.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) {
      return;
    }
    onCancel?.();
  };

  const isReviewStep = step === OPPORTUNITY_EDITOR_STEPS.length - 1;

  return (
    <>
      <UnsavedLeaveGuard enabled={dirty} />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <AdminEditorStepper
          currentStep={step}
          completedSteps={completedSteps}
          onStepChange={(index) => {
            const maxReachable = Math.max(-1, ...completedSteps, -1) + 1;
            if (index === step || completedSteps.includes(index) || index <= maxReachable) {
              setStep(index);
            }
          }}
        />
        <DraftStatusIndicator status={draftStatus} />
      </div>

      {step === 0 && (
        <DetailSection title="Basic information">
          <div className="row g-3">
            <div className="col-md-8">
              <TextInputComp LabelText="Title" name="title" required />
            </div>
            <div className="col-md-4">
              <TextInputComp LabelText="Slug" name="slug" placeholder="auto-generated if empty" />
            </div>
            <div className="col-md-4">
              <TextInputComp LabelText="Category" name="category" required />
            </div>
            <div className="col-md-4">
              <TextInputComp LabelText="Provider / manager" name="providerName" required />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Risk level</label>
              <select
                className="form-select investor-portal-note-input"
                value={values.riskLevel}
                onChange={(e) => setFieldValue("riskLevel", e.target.value)}
              >
                <option value="Low">Low</option>
                <option value="Moderate">Moderate</option>
                <option value="High">High</option>
              </select>
            </div>
            <div className="col-12">
              <FormikTextAreaComp LabelText="Short description" fieldName="shortDescription" rows={2} />
            </div>
            <div className="col-12">
              <FormikTextAreaComp
                LabelText="Full description (internal)"
                fieldName="fullDescription"
                rows={3}
              />
            </div>
          </div>
        </DetailSection>
      )}

      {step === 1 && (
        <DetailSection title="Financial terms">
          <div className="row g-3">
            <div className="col-md-3">
              <TextInputComp LabelText="Unit price (NGN)" name="unitPriceNgn" type="number" required />
            </div>
            <div className="col-md-3">
              <TextInputComp LabelText="Expected return %" name="expectedReturnPct" type="number" required />
            </div>
            <div className="col-md-3">
              <TextInputComp LabelText="Duration (months)" name="durationMonths" type="number" required />
            </div>
            <div className="col-md-3">
              <TextInputComp LabelText="Currency" name="currency" disabled />
            </div>
            <div className="col-md-3">
              <TextInputComp LabelText="Minimum units" name="minUnits" type="number" required />
            </div>
            <div className="col-md-3">
              <TextInputComp LabelText="Maximum units" name="maxUnits" type="number" required />
            </div>
            <div className="col-md-3">
              <TextInputComp LabelText="Total units" name="totalUnits" type="number" required />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold">Payout type</label>
              <select
                className="form-select investor-portal-note-input"
                value={values.payoutType}
                onChange={(e) => setFieldValue("payoutType", e.target.value)}
              >
                <option value="capital_plus_return">Capital + return</option>
                <option value="interest_only">Interest only</option>
              </select>
            </div>
          </div>
        </DetailSection>
      )}

      {step === 2 && (
        <DetailSection title="Dates & lifecycle">
          <p className="text-muted small mb-3">
            Chronological order: scheduled publish ≤ funding open &lt; funding close ≤ investment start
            &lt; maturity ≤ payout. Funding open/close and investment start may be today or in the past
            (for deals that began before UniqueCoop). Maturity and payout cannot be in the past. If a
            date order is wrong, you will see a field error when you continue.
          </p>
          <div className="row g-3">
            <div className="col-md-4">
              <TextInputComp
                LabelText="Scheduled publish"
                name="scheduledPublishAt"
                type="date"
                min={dateMins.scheduledPublishAt}
                max={dateMins.scheduledPublishAtMax}
                desc="When the opportunity becomes visible"
              />
            </div>
            <div className="col-md-4">
              <TextInputComp
                LabelText="Funding open"
                name="fundingOpenDate"
                type="date"
                required
                min={dateMins.fundingOpenDate}
                desc="Investors may start submitting requests"
              />
            </div>
            <div className="col-md-4">
              <TextInputComp
                LabelText="Funding close"
                name="fundingCloseDate"
                type="date"
                required
                min={dateMins.fundingCloseDate}
                desc="Final date for accepting requests"
              />
            </div>
            <div className="col-md-4">
              <TextInputComp
                LabelText="Investment start"
                name="investmentStartDate"
                type="date"
                required
                min={dateMins.investmentStartDate}
                desc="When the investment operation begins"
              />
            </div>
            <div className="col-md-4">
              <TextInputComp
                LabelText="Maturity date"
                name="maturityDate"
                type="date"
                required
                min={dateMins.maturityDate}
                desc="When the investment term ends"
              />
            </div>
            <div className="col-md-4">
              <TextInputComp
                LabelText="Return / payout date"
                name="returnPayoutDate"
                type="date"
                required
                min={dateMins.returnPayoutDate}
                desc="Expected capital or returns payout"
              />
            </div>
          </div>
        </DetailSection>
      )}

      {step === 3 && (
        <DetailSection title="Public content">
          <p className="text-muted small mb-3">
            These sections map directly to the investor-facing investment detail page.
          </p>
          <AdminContentSectionEditor
            values={values}
            onChange={(v) => Object.entries(v).forEach(([k, val]) => setFieldValue(k, val))}
          />
        </DetailSection>
      )}

      {step === 4 && (
        <DetailSection title="Media & documents">
          <p className="text-muted small mb-3">
            Document uploads are managed on the opportunity detail Documents tab after creation.
          </p>
          <CoverImageField
            value={values.coverImageUrl}
            onChange={(url) => setFieldValue("coverImageUrl", url)}
            error={errors.coverImageUrl}
          />
        </DetailSection>
      )}

      {step === 5 && (
        <DetailSection title="Review & publish">
          <ReviewSummary values={values} onEditStep={setStep} />
        </DetailSection>
      )}

      <div className="admin-editor-footer">
        <div className="admin-editor-footer-left">
          {step === 0 ? (
            <InvestorPortalActionButton
              btnText="Cancel"
              variant="ghost"
              fullWidth={false}
              type="button"
              onClick={handleExit}
            />
          ) : (
            <InvestorPortalActionButton
              btnText="Previous"
              variant="outline"
              fullWidth={false}
              type="button"
              onClick={() => setStep((s) => s - 1)}
            />
          )}
        </div>

        <div className="admin-editor-footer-right">
          {!isReviewStep ? (
            <>
              {step > 0 && onCancel ? (
                <InvestorPortalActionButton
                  btnText="Exit"
                  variant="ghost"
                  fullWidth={false}
                  type="button"
                  onClick={handleExit}
                />
              ) : null}
              <InvestorPortalActionButton
                btnText="Save and Continue"
                variant="primary"
                fullWidth={false}
                type="button"
                disabled={isSubmitting}
                onClick={handleSaveAndContinue}
              />
            </>
          ) : (
            <>
              <InvestorPortalActionButton
                btnText="Save as Draft"
                variant="ghost"
                fullWidth={false}
                type="button"
                disabled={isSubmitting}
                onClick={() => handleLifecycleAction("draft")}
              />
              <AdminPublicPreviewAction opportunity={values} fullWidth={false} label="Preview" />
              {canPublishNow ? (
                <InvestorPortalActionButton
                  btnText="Publish"
                  variant="primary"
                  fullWidth={false}
                  type="button"
                  disabled={isSubmitting || !checklist.ready}
                  onClick={() => handleLifecycleAction("publish")}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function OpportunityEditor({
  mode = "create",
  initialOpportunity,
  initialStep = 0,
  onSave,
  onCancel,
}) {
  const initialValues = useMemo(() => toFormValues(initialOpportunity), [initialOpportunity]);
  const [step, setStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState(() => deriveCompletedSteps(initialValues));
  const [draftStatus, setDraftStatus] = useState("idle");

  useEffect(() => {
    setCompletedSteps(deriveCompletedSteps(initialValues));
  }, [initialValues]);

  useEffect(() => {
    if (typeof initialStep === "number" && initialStep > 0) {
      setStep(initialStep);
    }
  }, [initialStep]);

  const editorStatus = initialOpportunity?.status || initialValues.status || OPPORTUNITY_STATUS.DRAFT;
  const canPublishNow = [
    OPPORTUNITY_STATUS.DRAFT,
    OPPORTUNITY_STATUS.INTERNAL_REVIEW,
    OPPORTUNITY_STATUS.SCHEDULED,
  ].includes(editorStatus);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={() => {}}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <Form className="investor-portal-form-grid">
        <EditorBody
          step={step}
          setStep={setStep}
          completedSteps={completedSteps}
          setCompletedSteps={setCompletedSteps}
          draftStatus={draftStatus}
          setDraftStatus={setDraftStatus}
          mode={mode}
          onSave={onSave}
          onCancel={onCancel}
          canPublishNow={canPublishNow}
        />
      </Form>
    </Formik>
  );
}
